import Image from "next/image"

export default function ComingSoonPage() {
  return (
    <div className="flex min-h-screen flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
          <Image
            src="/logo.png"
            alt="ServicePro Logo"
            width={160}
            height={40}
            className="h-10 w-auto"
            priority
          />
          <a
            href="#powiadom"
            className="rounded-lg bg-gradient-to-r from-[#F26539] to-[#F6871F] px-5 py-2.5 text-sm font-semibold text-white transition-all hover:opacity-90 hover:shadow-lg"
          >
            Powiadom mnie
          </a>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative overflow-hidden py-20 md:py-32">
          <div className="absolute inset-0 -z-10">
            <div className="absolute right-0 top-0 h-96 w-96 -translate-y-1/2 translate-x-1/2 rounded-full bg-gradient-to-br from-[#009DE1]/10 to-[#212E65]/10 blur-3xl" />
            <div className="absolute bottom-0 left-0 h-96 w-96 -translate-x-1/2 translate-y-1/2 rounded-full bg-gradient-to-br from-[#F26539]/10 to-[#F6871F]/10 blur-3xl" />
          </div>

          <div className="container mx-auto max-w-6xl px-4">
            <div className="mx-auto max-w-3xl text-center">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2 text-sm font-medium text-primary">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-primary"></span>
                </span>
                Wkrótce start
              </div>

              <h1 className="mb-6 text-balance text-4xl font-bold tracking-tight text-foreground md:text-5xl lg:text-6xl">
                Wkrótce uruchomimy możliwość{" "}
                <span className="bg-gradient-to-r from-[#009DE1] to-[#212E65] bg-clip-text text-transparent">
                  zakupu reklamy online
                </span>
              </h1>

              <p className="mb-10 text-pretty text-lg text-muted-foreground md:text-xl">
                Pracujemy nad platformą, dzięki której szybko i wygodnie zamówisz
                reklamę oraz promocję swojej oferty.
              </p>

              <a
                href="#powiadom"
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#F26539] to-[#F6871F] px-8 py-4 text-lg font-semibold text-white transition-all hover:opacity-90 hover:shadow-xl"
              >
                Powiadom mnie
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="border-t border-border/40 bg-muted/30 py-16 md:py-24">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="mb-6 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
                O platformie{" "}
                <span className="bg-gradient-to-r from-[#009DE1] to-[#212E65] bg-clip-text text-transparent">
                  ServicePro
                </span>
              </h2>
              <p className="text-pretty text-lg leading-relaxed text-muted-foreground">
                ServicePro to nowoczesna platforma do zakupu reklamy online. Naszym
                celem jest uproszczenie procesu zamawiania i zarządzania kampaniami
                reklamowymi. Dzięki intuicyjnemu interfejsowi i przejrzystym cenom,
                każdy przedsiębiorca będzie mógł skutecznie promować swoją ofertę.
              </p>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
                Jak to będzie działać?
              </h2>
              <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
                Trzy proste kroki do skutecznej promocji Twojej oferty
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              {[
                {
                  number: "01",
                  title: "Wybierz format",
                  description: "Przeglądaj dostępne formaty reklamowe i wybierz ten, który najlepiej pasuje do Twoich potrzeb.",
                  icon: <path d="M9 3v18M3 9h6" />,
                  rect: true,
                },
                {
                  number: "02",
                  title: "Dostosuj kampanię",
                  description: "Skonfiguruj szczegóły kampanii, dodaj materiały reklamowe i określ grupę docelową.",
                  icon: <><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></>,
                },
                {
                  number: "03",
                  title: "Zamów i płać online",
                  description: "Finalizuj zamówienie bezpieczną płatnością online i obserwuj wyniki w czasie rzeczywistym.",
                  icon: <><rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" /></>,
                },
              ].map((step, index) => (
                <div
                  key={step.number}
                  className="group relative rounded-2xl border border-border/60 bg-card p-8 transition-all hover:border-primary/40 hover:shadow-lg"
                >
                  {index < 2 && (
                    <div className="absolute right-0 top-1/2 hidden h-px w-8 -translate-y-1/2 translate-x-full bg-gradient-to-r from-border to-transparent md:block" />
                  )}
                  <div className="mb-6 flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#009DE1] to-[#212E65] text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        {step.rect && <rect width="18" height="18" x="3" y="3" rx="2" />}
                        {step.icon}
                      </svg>
                    </div>
                    <span className="text-4xl font-bold text-border group-hover:text-primary/30 transition-colors">
                      {step.number}
                    </span>
                  </div>
                  <h3 className="mb-3 text-xl font-semibold text-foreground">{step.title}</h3>
                  <p className="leading-relaxed text-muted-foreground">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="border-t border-border/40 bg-muted/30 py-16 md:py-24">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
                Dlaczego warto?
              </h2>
              <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
                Poznaj korzyści płynące z korzystania z platformy ServicePro
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { title: "Szybko i wygodnie", description: "Zamów reklamę w kilka minut, bez zbędnych formalności i oczekiwania na ofertę.", icon: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></> },
                { title: "Przejrzyste ceny", description: "Wszystkie ceny widoczne od razu, bez ukrytych kosztów i niespodzianek.", icon: <><line x1="12" x2="12" y1="2" y2="22" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></> },
                { title: "Pełna kontrola", description: "Zarządzaj kampaniami z jednego miejsca i monitoruj wyniki w czasie rzeczywistym.", icon: <><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></> },
                { title: "Wsparcie ekspertów", description: "Nasz zespół pomoże Ci wybrać najlepsze rozwiązania dla Twojego biznesu.", icon: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></> },
              ].map((benefit) => (
                <div
                  key={benefit.title}
                  className="rounded-xl border border-border/60 bg-card p-6 transition-all hover:border-secondary/40 hover:shadow-lg"
                >
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-[#F26539] to-[#F6871F] text-white">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {benefit.icon}
                    </svg>
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-foreground">{benefit.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="powiadom" className="py-16 md:py-24">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mx-auto max-w-2xl rounded-2xl border border-border/60 bg-gradient-to-b from-card to-muted/30 p-8 text-center shadow-lg md:p-12">
              <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#009DE1] to-[#212E65] text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                  <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                </svg>
              </div>

              <h2 className="mb-4 text-2xl font-bold tracking-tight text-foreground md:text-3xl">
                Bądź na bieżąco
              </h2>
              <p className="mb-8 text-muted-foreground">
                Zostaw swój e-mail, a powiadomimy Cię, gdy platforma zostanie
                uruchomiona. Żadnego spamu — tylko ważne informacje.
              </p>

              <a
                href="mailto:kontakt@servicepro.pl?subject=Powiadomienie%20o%20starcie%20platformy"
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#F26539] to-[#F6871F] px-8 py-4 text-lg font-semibold text-white transition-all hover:opacity-90 hover:shadow-xl"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
                Napisz do nas
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/40 bg-muted/30 py-8">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <Image
              src="/logo.png"
              alt="ServicePro Logo"
              width={120}
              height={30}
              className="h-8 w-auto opacity-70"
            />
            <p className="text-sm text-muted-foreground">
              © 2026 ServicePro. Wszelkie prawa zastrzeżone.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
